import { Fragment, useEffect, useState } from "react";
import { Accounts } from "./Accounts";
import { Bank } from "./Bank";
import { ApiError, api, type SessionInfo } from "./api";
import { Categories } from "./Categories";
import { Company } from "./Company";
import { Users } from "./Users";
import { ChangePassword } from "./ChangePassword";
import { Devices } from "./Devices";
import { ContentLayout } from "./layout/Layout";
import { go, type Page, useHash, usePage } from "./layout/router";
import { Pos } from "./pos/Pos";
import { People } from "./People";
import { Claims } from "./Claims";
import { Dashboard } from "./Dashboard";
import { Progress } from "./Progress";
import { Reports } from "./Reports";
import { Sales } from "./Sales";
import { Purchases } from "./Purchases";
import { SignIn } from "./SignIn";
import { Stock } from "./Stock";
import { Loading } from "./ui";
import { MoneyPage } from "./MoneyLedger";

type RoleName = SessionInfo["user"]["role"];

/** These mirror the server's permissions, which are the authority. They only decide what is shown. */
const SUPERVISOR_UP: readonly RoleName[] = ["supervisor", "manager", "admin", "master"];
const MANAGER_UP: readonly RoleName[] = ["manager", "admin", "master"];
const ADMIN_UP: readonly RoleName[] = ["admin", "master"];

/** The browser tab reads "OHC - Stock" and so on, as the template's pages set theirs. */
const TITLES: Record<Page, string> = {
  home: "Dashboard",
  progress: "System development status",
  pos: "Sell now",
  sales: "Sales and buybacks",
  people: "Staff balances",
  reports: "Reports",
  claims: "Expense claims",
  stock: "Inventory",
  purchases: "Inventory purchases",
  expenses: "Expenses",
  accounts: "Financial accounts",
  bank: "Bank reconciliation",
  categories: "Transaction categories",
  devices: "Devices",
  company: "Company profile",
  users: "User access",
  password: "Sign-in settings",
  retail: "Retail sales",
  tradeshow: "Tradeshow sales",
  tiktok: "TikTok sales",
  buybacks: "Card buybacks",
  money: "Company accounts",
};

/** Pages a role is allowed to open. Anything else falls back to the dashboard. */
function allowed(page: Page, role: RoleName): boolean {
  // Sales staff see the selling screen and nothing else (D, 1 November 2026).
  if (role === "sales_staff") return page === "pos";
  // The money side is for managers and above (D, 8 October 2026).
  if (page === "purchases" || page === "expenses" || page === "claims" || page === "accounts" || page === "people") return MANAGER_UP.includes(role);
  if (page === "progress") return role === "master";
  if (page === "bank" || page === "money") return MANAGER_UP.includes(role);
  if (page === "reports") return MANAGER_UP.includes(role);
  if (page === "devices") return role === "master";
  if (page === "company") return role === "master";
  if (page === "users") return role === "master";
  return true;
}

/**
 * Who was signed in on this browser, kept so that an iPad with no signal can still open
 * the POS it was signed in to. It is only ever used when the server cannot be reached:
 * the server's own answer always wins, and signing out forgets it.
 */
const REMEMBERED = "ohc.session";

function remembered(): SessionInfo | undefined {
  try {
    const text = window.localStorage.getItem(REMEMBERED);
    return text ? (JSON.parse(text) as SessionInfo) : undefined;
  } catch {
    return undefined;
  }
}

function remember(session: SessionInfo | undefined): void {
  try {
    if (session) window.localStorage.setItem(REMEMBERED, JSON.stringify(session));
    else window.localStorage.removeItem(REMEMBERED);
  } catch {
    // Nothing kept in a private window: offline, it asks to sign in.
  }
}

export function App(): React.ReactElement {
  const [session, setSession] = useState<SessionInfo | undefined>();
  const [checking, setChecking] = useState(true);
  const page = usePage();
  const hash = useHash();
  // The refresh in the header starts the page again with fresh figures.
  const [fresh, setFresh] = useState(0);
  useEffect(() => {
    const again = (): void => setFresh((count) => count + 1);
    window.addEventListener("ohc:refresh", again);
    return () => window.removeEventListener("ohc:refresh", again);
  }, []);

  useEffect(() => {
    let live = true;
    api
      .me()
      .then((result) => {
        if (live) setSession(result);
      })
      .catch((problem: unknown) => {
        if (!live) return;
        if (problem instanceof ApiError && problem.status === 401) {
          // Nobody is signed in on this browser, or the session ended: forget it.
          remember(undefined);
        } else if (problem instanceof ApiError && problem.status === 0) {
          // No connection: carry on as whoever was signed in here, if anyone.
          const last = remembered();
          if (last) setSession(last);
        } else {
          console.error(problem);
        }
      })
      .finally(() => {
        if (live) setChecking(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (session) remember(session);
  }, [session]);

  const signedIn = session !== undefined && !session.user.mustChangePassword;
  const shownPage: Page = session && allowed(page, session.user.role) ? page : session?.user.role === "sales_staff" ? "pos" : "home";
  useEffect(() => {
    document.title = signedIn ? `OHC - ${TITLES[shownPage]}` : "OHC - Sign in";
  }, [signedIn, shownPage]);

  if (checking) return <Loading />;
  if (!session) return <SignIn onSignedIn={setSession} />;

  if (session.user.mustChangePassword) {
    return (
      <ChangePassword
        forced
        onDone={() => setSession({ ...session, user: { ...session.user, mustChangePassword: false } })}
      />
    );
  }

  async function signOut(): Promise<void> {
    await api.signOut().catch(() => undefined);
    remember(undefined);
    setSession(undefined);
    go("home");
  }

  const role = session.user.role;
  const current = shownPage;

  // The booth screen takes the whole screen, with no sidebar or header of the desk.
  if (current === "pos") return <Pos session={session} onSignOut={() => void signOut()} />;

  return (
    <ContentLayout session={session} page={current} onSignOut={() => void signOut()}>
      <Fragment key={fresh}>
      {current === "home" ? (
        <Dashboard fullName={session.user.fullName} />
      ) : current === "stock" ? (
        <Stock
          canManage={MANAGER_UP.includes(role)}
          canSetPrice={SUPERVISOR_UP.includes(role)}
          canAdjust={MANAGER_UP.includes(role)}
          canBuyFromStaff={ADMIN_UP.includes(role)}
          canCheck={ADMIN_UP.includes(role)}
        />
      ) : current === "sales" || current === "retail" || current === "tradeshow" || current === "tiktok" || current === "buybacks" ? (
        <Sales
          key={current}
          mode={current === "sales" ? "all" : current}
          canReverse={MANAGER_UP.includes(role)}
          canFinish={MANAGER_UP.includes(role)}
          canVoid={SUPERVISOR_UP.includes(role)}
          canInvoice={SUPERVISOR_UP.includes(role)}
        />
      ) : current === "claims" ? (
        <Claims fullName={session.user.fullName} />
      ) : current === "reports" ? (
        <Reports />
      ) : current === "people" ? (
        <People />
      ) : current === "purchases" ? (
        <Purchases key="inventory" mode="inventory" canVerify={ADMIN_UP.includes(role)} canHandleVouchers={ADMIN_UP.includes(role)} />
      ) : current === "expenses" ? (
        <Purchases key="expenses" mode="expenses" canVerify={ADMIN_UP.includes(role)} canHandleVouchers={ADMIN_UP.includes(role)} />
      ) : current === "accounts" ? (
        <Accounts canManage={ADMIN_UP.includes(role)} />
      ) : current === "bank" ? (
        <Bank key={hash} />
      ) : current === "money" ? (
        <MoneyPage key={hash} />
      ) : current === "categories" ? (
        <Categories />
      ) : current === "devices" ? (
        <Devices />
      ) : current === "company" ? (
        <Company canManage={role === "master"} />
      ) : current === "users" ? (
        <Users />
      ) : current === "password" ? (
        <ChangePassword forced={false} onDone={() => go("home")} />
      ) : (
        <Progress />
      )}
      </Fragment>
    </ContentLayout>
  );
}
